/**
 * SERVICE_CATEGORIES — top-level service taxonomy.
 *
 * Categories are now ADMIN-MANAGED via /api/products/services. This file
 * holds:
 *  - A static metadata map (icons + spec lines + long-form copy) for the
 *    original 7 built-in slugs so the storefront keeps its rich visuals.
 *  - The legacy SERVICE_CATEGORIES const which is the same fallback list,
 *    kept exported for any non-hook callers that want a sync constant.
 *  - useServiceCategories() — hook that hits the backend and merges in
 *    static metadata when available; admin-created categories without a
 *    metadata entry render with sensible defaults.
 */
import { useEffect, useState } from "react";
import {
  Tag, Printer, Layers, Flag, Megaphone, BookOpen, FolderOpen, Sparkles,
} from "lucide-react";
import { api } from "./api";

// Per-slug static enrichment for the original 7 built-in categories.
// Anything missing in this map (i.e. admin-created categories) falls
// back to defaults at render time.
const STATIC_METADATA = {
  "roll-labels": {
    short: "Custom roll labels for every product on your shelf.",
    longDesc:
      "Paper, BOPP, vinyl, foil and eco-kraft roll labels — plus custom die-cut shapes. Quantity discounts kick in fast and run all the way up to 50,000 per order.",
    icon: Tag,
    spec: "22 SKUs · 0.5\" – 8\" · 250 – 50,000 qty",
  },
  "digital-print": {
    short: "Fast-turn short runs that look amazing.",
    longDesc:
      "Brochures, flyers, business cards and folded marketing pieces — printed digitally for fast, affordable short and mid-volume runs. Same-day cutoff at 1pm CT.",
    icon: Printer,
    spec: "10 SKUs · Short-run · CMYK + spot",
  },
  "offset-print": {
    short: "High-volume prints with rich, accurate color.",
    longDesc:
      "Sheet-fed offset printing for big runs — catalogs, retail packaging and premium marketing pieces. Pantone-matched ink and crisp 4-color (or 6-color) capability.",
    icon: Layers,
    spec: "10 SKUs · 6-color press · Spot UV / Foil",
  },
  marketing: {
    short: "Postcards, flyers and direct-mail that gets opened.",
    longDesc:
      "Direct-mail postcards, EDDM, rack cards and door hangers. Reliable production times so your campaign hits when it's supposed to.",
    icon: Megaphone,
    spec: "10 SKUs · EDDM · 4×6 to 11×17",
  },
  "retractables-banners": {
    short: "Trade-show banners and large-format displays.",
    longDesc:
      "Retractable banners, step-and-repeat backdrops, vinyl outdoor signs and table-top displays. Hardware and replacement-graphic kits included.",
    icon: Flag,
    spec: "10 SKUs · 33\" – 96\" wide · Stand included",
  },
  "booklets-books": {
    short: "Saddle-stitched and perfect-bound, beautifully made.",
    longDesc:
      "Annual reports, catalogs, lookbooks, manuals and journals. Saddle-stitch, perfect-bind and Wire-O binding — anywhere from 8 to 400 pages.",
    icon: BookOpen,
    spec: "10 SKUs · Saddle · Perfect-bind · 8 – 400 pp",
  },
  "presentation-folders": {
    short: "Pocket folders for sales kits and onboarding.",
    longDesc:
      "Single and dual-pocket presentation folders with business-card slits, embossing and foil-stamp finishes. Sharp die-cut edges, premium feel.",
    icon: FolderOpen,
    spec: "10 SKUs · 1 or 2 pocket · 14pt – 18pt cover",
  },
};

// Merge a DB category record with its static metadata (when available).
function enrich(cat) {
  const meta = STATIC_METADATA[cat.slug] || {};
  return {
    slug: cat.slug,
    name: cat.name,
    short: meta.short || cat.description || "",
    longDesc: meta.longDesc || cat.description || "",
    icon: meta.icon || Sparkles,
    spec: meta.spec || (cat.product_count != null ? `${cat.product_count} products` : ""),
    image: cat.image_url || "",
    available: true,
    product_count: cat.product_count ?? null,
    href: cat.href,
    virtual: !!cat.virtual,
  };
}

// Legacy synchronous export — built from the static map. Provides the
// 7 built-in categories with proper names so any pre-existing synchronous
// callers keep rendering richly while the backend list loads.
const STATIC_NAMES = {
  "roll-labels": "Roll Labels",
  "digital-print": "Digital Print",
  "offset-print": "Offset Print",
  "marketing": "Marketing",
  "retractables-banners": "Retractables & Banners",
  "booklets-books": "Booklets & Books",
  "presentation-folders": "Presentation Folders",
};
export const SERVICE_CATEGORIES = Object.entries(STATIC_METADATA).map(
  ([slug, meta]) => ({ slug, name: STATIC_NAMES[slug] || slug, ...meta, available: true })
);

export function getCategoryBySlug(slug) {
  return SERVICE_CATEGORIES.find((c) => c.slug === slug);
}

// Reverse alias map: DB slug → pretty URL slug. Mirrors the aliases in
// ShopPage.jsx so navbar/footer/product links emit the pretty URL directly
// (avoids the 301 hop on the first click).
const DB_TO_PRETTY_SLUG = {
  "digital-print": "marketing-collateral",
  "retractables-banners": "large-format",
};

/**
 * categoryHref(slug) — returns the canonical /shop URL for a DB category slug.
 * Applies the pretty-alias mapping so users see the marketing URL, not the
 * legacy DB slug. Example: "retractables-banners" → "/shop/large-format".
 */
export function categoryHref(slug, subSlug = "") {
  const virt = VIRTUAL_NAV_CATEGORIES.find((v) => v.slug === slug);
  if (virt) return virt.href;
  const pretty = DB_TO_PRETTY_SLUG[slug] || slug;
  return subSlug ? `/shop/${pretty}/${subSlug}` : `/shop/${pretty}`;
}

// ── Navigation overrides ─────────────────────────────────────────────────────
// Categories hidden from every menu / strip / home grid (still reachable by URL).
export const HIDDEN_NAV_SLUGS = new Set(["custom-presentation-folders-pocket-folders", "presentation-folders"]);
// Virtual nav entries that point at a sub-category page.
export const VIRTUAL_NAV_CATEGORIES = [{
  slug: "wedding-collateral", name: "Wedding Collateral", available: true, product_count: null, virtual: true,
  href: "/shop/marketing-collateral/wedding-collateral",
  short: "Invitations, save-the-dates, menus, programs, place cards, favor labels and welcome signs.",
  description: "Invitations, save-the-dates, menus, programs, place cards, favor labels and welcome signs — with free stationer tools for sizes, counts and postage.",
  image_url: "",
}];
export function applyNavOverrides(list) {
  const base = (list || []).filter((c) => !HIDDEN_NAV_SLUGS.has(c.slug));
  return [...base, ...VIRTUAL_NAV_CATEGORIES.filter((v) => !base.some((c) => c.slug === v.slug))];
}

// Tiny in-memory cache shared across all hook instances so navbar, shop,
// home page etc. don't each fire their own GET on mount.
let _cache = null;
let _promise = null;

async function fetchServices() {
  if (_cache) return _cache;
  if (_promise) return _promise;
  _promise = api.get("/products/services").then(
    ({ data }) => { _cache = applyNavOverrides(data || []); _promise = null; return _cache; },
    () => { _promise = null; return []; },
  );
  return _promise;
}

export function invalidateServiceCategories() { _cache = null; }

/**
 * useServiceCategories — returns the live, admin-managed list.
 *
 * Each entry has: {slug, name, short, longDesc, icon, spec, image,
 * available, product_count}. Static metadata is merged in for the 7
 * built-in slugs; admin-created slugs fall back to sensible defaults.
 */
export function useServiceCategories() {
  const [items, setItems] = useState(() =>
    (_cache || applyNavOverrides(SERVICE_CATEGORIES)).map(enrich),
  );
  const [loading, setLoading] = useState(!_cache);

  useEffect(() => {
    let alive = true;
    fetchServices().then((data) => {
      if (alive) {
        setItems(data.map(enrich));
        setLoading(false);
      }
    });
    return () => { alive = false; };
  }, []);

  return { categories: items, loading };
}
